import { computed, onUnmounted, ref, watch } from 'vue';

interface UsePriceRangeFilterOptions {
    /** Absolute lower bound of the slider (props.min) */
    min: () => number;
    /** Absolute upper bound of the slider (props.max) */
    max: () => number;
    /** Debounce in ms before pushing changes to the URL */
    debounceMs?: number;
}

/**
 * Encapsulates the dual-handle price range filter used by the PLP price filter.
 *
 * Why this composable exists:
 * - The displayed value of the input is decoupled from `minPrice` / `maxPrice`,
 *   so reactive updates from the URL/route never overwrite what the user is
 *   currently typing. (Previously this caused the max input to "reset to the
 *   previous value" on mobile while the user was still entering it.)
 * - The debounced URL push waits for both inputs to be unfocused; otherwise it
 *   would push prematurely while the user is mid-typing, which on mobile can
 *   dismiss the virtual keyboard and trigger a focusout / value-reset cascade.
 */
export function usePriceRangeFilter(options: UsePriceRangeFilterOptions) {
    const { debounceMs = 1000 } = options;
    const router = useRouter();
    const route = useRoute();

    const formatPriceDisplay = (value: number | string): string => {
        const numValue = Number(value);
        if (isNaN(numValue)) return '';
        return numValue.toString().replace('.', ',');
    };

    const formatPrice = (value: number | string): string => {
        const numValue = Number(value);
        if (isNaN(numValue)) return '';
        return numValue % 1 === 0 ? numValue.toFixed(0) : numValue.toFixed(2);
    };

    const parsePriceInputValue = (raw: string): number | null => {
        const value = raw.replace('€', '').trim().replace(',', '.');
        const newValue = parseFloat(value);
        return isNaN(newValue) ? null : newValue;
    };

    const initialMin = route.query['min-price'] ? Number(route.query['min-price']) : options.min();
    const initialMax = route.query['max-price'] ? Number(route.query['max-price']) : options.max();

    const minPrice = ref<number>(initialMin);
    const maxPrice = ref<number>(initialMax);

    // Decoupled display state: what the user actually sees in the input.
    // We never overwrite this from URL/route reactivity while the user is
    // focused on the corresponding input, so their typing is preserved.
    const minPriceInput = ref<string>(formatPriceDisplay(initialMin));
    const maxPriceInput = ref<string>(formatPriceDisplay(initialMax));

    const isMinPriceInputFocused = ref(false);
    const isMaxPriceInputFocused = ref(false);

    const minInputRef = ref<HTMLInputElement | null>(null);
    const maxInputRef = ref<HTMLInputElement | null>(null);

    let applyTimeout: ReturnType<typeof setTimeout> | null = null;

    const cancelPendingApply = () => {
        if (applyTimeout) {
            clearTimeout(applyTimeout);
            applyTimeout = null;
        }
    };

    const debouncedApplyFilter = () => {
        cancelPendingApply();
        applyTimeout = setTimeout(() => {
            applyTimeout = null;
            // Defer if the user is still interacting with either input.
            // The next focusout will reschedule the apply.
            if (isMinPriceInputFocused.value || isMaxPriceInputFocused.value) {
                return;
            }
            applyPriceFilter();
        }, debounceMs);
    };

    const applyPriceFilter = () => {
        router.push({
            path: route.path,
            query: {
                ...route.query,
                'min-price': minPrice.value || undefined,
                'max-price': maxPrice.value || undefined,
            },
        });
    };

    // Keep the displayed input strings in sync with the committed refs whenever
    // the user is NOT actively editing that field. This handles URL-driven and
    // slider-driven changes without clobbering in-progress typing.
    watch(minPrice, value => {
        if (!isMinPriceInputFocused.value) {
            minPriceInput.value = formatPriceDisplay(value);
        }
    });

    watch(maxPrice, value => {
        if (!isMaxPriceInputFocused.value) {
            maxPriceInput.value = formatPriceDisplay(value);
        }
    });

    watch(
        () => route.query,
        newQuery => {
            if (!isMinPriceInputFocused.value) {
                const newMin = newQuery['min-price'] ? Number(newQuery['min-price']) : options.min();
                minPrice.value = newMin;
            }
            if (!isMaxPriceInputFocused.value) {
                const newMax = newQuery['max-price'] ? Number(newQuery['max-price']) : options.max();
                maxPrice.value = newMax;
            }
        },
        { immediate: true },
    );

    const onMinPriceFocus = (event: FocusEvent) => {
        isMinPriceInputFocused.value = true;
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
            (event.target as HTMLInputElement).select();
        }
    };

    const onMaxPriceFocus = (event: FocusEvent) => {
        isMaxPriceInputFocused.value = true;
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
            (event.target as HTMLInputElement).select();
        }
    };

    /**
     * Validate and commit the typed min value on blur.
     *
     * Behavior on invalid input is intentionally non-destructive:
     * - NaN -> revert displayed value to current minPrice (user's typing is
     *   unrecoverable anyway).
     * - >= maxPrice -> auto-correct to (maxPrice - 1) clamped within range,
     *   instead of silently resetting to the previous min. This is much less
     *   surprising than resetting their typed value.
     */
    const onMinPriceFocusOut = () => {
        isMinPriceInputFocused.value = false;
        const parsed = parsePriceInputValue(minPriceInput.value);
        if (parsed === null) {
            minPriceInput.value = formatPriceDisplay(minPrice.value);
            return;
        }
        const clamped = Math.min(Math.max(parsed, options.min()), options.max());
        let nextValue = clamped;
        if (nextValue >= Number(maxPrice.value)) {
            nextValue = Math.max(options.min(), Number(maxPrice.value) - 1);
        }
        minPrice.value = nextValue;
        minPriceInput.value = formatPriceDisplay(nextValue);
        debouncedApplyFilter();
    };

    /**
     * Validate and commit the typed max value on blur. Mirrors min behavior.
     */
    const onMaxPriceFocusOut = () => {
        isMaxPriceInputFocused.value = false;
        const parsed = parsePriceInputValue(maxPriceInput.value);
        if (parsed === null) {
            maxPriceInput.value = formatPriceDisplay(maxPrice.value);
            return;
        }
        const clamped = Math.min(Math.max(parsed, options.min()), options.max());
        let nextValue = clamped;
        if (nextValue <= Number(minPrice.value)) {
            nextValue = Math.min(options.max(), Number(minPrice.value) + 1);
        }
        maxPrice.value = nextValue;
        maxPriceInput.value = formatPriceDisplay(nextValue);
        debouncedApplyFilter();
    };

    const leftHandlePercent = computed(() => {
        const range = options.max() - options.min();
        if (range <= 0) return 0;
        return ((Number(minPrice.value) - options.min()) / range) * 100;
    });

    const rightHandlePercent = computed(() => {
        const range = options.max() - options.min();
        if (range <= 0) return 100;
        return ((Number(maxPrice.value) - options.min()) / range) * 100;
    });

    const sliderContainer = ref<HTMLElement | null>(null);
    const activeHandle = ref<'min' | 'max' | null>(null);
    const isDragging = ref(false);

    const handleDrag = (event: MouseEvent | TouchEvent) => {
        if (!isDragging.value || !sliderContainer.value) return;

        const rect = sliderContainer.value.getBoundingClientRect();
        const containerWidth = rect.width;

        const clientX =
            event.type === 'touchmove' ? (event as TouchEvent).touches[0].clientX : (event as MouseEvent).clientX;

        let position = (clientX - rect.left) / containerWidth;
        position = Math.max(0, Math.min(1, position));

        const range = options.max() - options.min();
        const value = Math.round(options.min() + position * range);

        if (activeHandle.value === 'min') {
            minPrice.value = Math.min(value, Number(maxPrice.value));
        } else if (activeHandle.value === 'max') {
            maxPrice.value = Math.max(value, Number(minPrice.value));
        }

        debouncedApplyFilter();
    };

    const stopDrag = () => {
        isDragging.value = false;
        activeHandle.value = null;
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', stopDrag);
        window.removeEventListener('touchmove', handleDrag);
        window.removeEventListener('touchend', stopDrag);
    };

    const startDrag = (event: MouseEvent | TouchEvent, handle: 'min' | 'max') => {
        event.preventDefault();
        stopDrag();
        activeHandle.value = handle;
        isDragging.value = true;

        window.addEventListener('mousemove', handleDrag);
        window.addEventListener('mouseup', stopDrag);
        window.addEventListener('touchmove', handleDrag);
        window.addEventListener('touchend', stopDrag);
    };

    onUnmounted(() => {
        cancelPendingApply();
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', stopDrag);
        window.removeEventListener('touchmove', handleDrag);
        window.removeEventListener('touchend', stopDrag);
    });

    return {
        minPrice,
        maxPrice,
        minPriceInput,
        maxPriceInput,
        minInputRef,
        maxInputRef,
        leftHandlePercent,
        rightHandlePercent,
        sliderContainer,
        onMinPriceFocus,
        onMaxPriceFocus,
        onMinPriceFocusOut,
        onMaxPriceFocusOut,
        startDrag,
        formatPrice,
        formatPriceDisplay,
    };
}
